import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Radio,
  RadioGroup,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import React, { useEffect, useMemo, useState } from 'react';

import { DispositionTree } from '../../../@types/connect.type';
import { flattenDispositionTree, groupLeavesByRoot } from '../../../utils/disposition-tree';
import { useConnectOptional } from '../ConnectProvider';

// After-call wrap-up modal. Auto-opens whenever `pendingDisposition` is
// set in ConnectContext (i.e. an inbound or outbound call just ended).
// Agent picks a category from the tree fetched on open + optionally adds
// notes. Submission posts to the support lambda and clears the prompt;
// Skip dismisses without writing anything to the CTR.

export const DispositionDialog: React.FC = () => {
  const ctx = useConnectOptional();
  const [tree, setTree] = useState<DispositionTree | null>(null);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selected, setSelected] = useState<string>('');
  const [notes, setNotes] = useState('');

  const pending = ctx?.pendingDisposition ?? null;
  const open = pending !== null;

  // Fetch the category tree once per opening. Reset all transient state so
  // a fresh modal never carries the previous call's draft notes. The
  // `cancelled` flag guards against late-resolving fetches from a previous
  // opening overwriting the current tree (e.g. agent dismisses + new call
  // ends before the first request returns).
  useEffect(() => {
    if (!ctx || !pending) return;
    let cancelled = false;
    setTree(null);
    setSelected('');
    setNotes('');
    setError(null);
    setLoading(true);
    ctx
      .fetchDispositionTree(pending.channel)
      .then((t) => {
        if (!cancelled) setTree(t);
      })
      .catch((e: unknown) => {
        if (!cancelled) {
          setError(e instanceof Error ? e.message : 'Failed to load wrap-up categories');
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [ctx, pending]);

  const groups = useMemo(() => groupLeavesByRoot(flattenDispositionTree(tree)), [tree]);

  if (!ctx || !pending) return null;

  const onSubmit = async () => {
    if (!selected) {
      setError('Pick a category before submitting.');
      return;
    }
    setError(null);
    setSubmitting(true);
    try {
      await ctx.submitDisposition(pending.contactId, pending.initialContactId, selected, notes);
      ctx.dismissDisposition();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to save wrap-up');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onClose={ctx.dismissDisposition} fullWidth maxWidth="sm">
      <DialogTitle>
        <Stack>
          <span>Wrap up call</span>
          {pending.queueName && (
            <Typography variant="caption" color="text.secondary">
              Queue: {pending.queueName}
            </Typography>
          )}
        </Stack>
      </DialogTitle>
      <DialogContent dividers>
        {loading ? (
          <Typography variant="body2" color="text.secondary">
            Loading categories…
          </Typography>
        ) : groups.length === 0 ? (
          <Alert severity="info">No wrap-up categories configured for this channel.</Alert>
        ) : (
          <RadioGroup value={selected} onChange={(_, v) => setSelected(v)}>
            {groups.map((group) => (
              <Box key={group.root} sx={{ mb: 1.5 }}>
                <Typography variant="caption" sx={{ fontWeight: 600, color: 'text.secondary' }}>
                  {group.root}
                </Typography>
                {group.leaves.map((leaf) => {
                  const detail = leaf.trail.slice(1, -1).join(' → ');
                  return (
                    <FormControlLabel
                      key={leaf.path}
                      value={leaf.path}
                      control={<Radio size="small" />}
                      label={
                        <Box>
                          <Typography variant="body2">{leaf.label}</Typography>
                          {detail && (
                            <Typography variant="caption" color="text.secondary">
                              {detail}
                            </Typography>
                          )}
                        </Box>
                      }
                      sx={{ display: 'flex', ml: 0.5 }}
                    />
                  );
                })}
              </Box>
            ))}
          </RadioGroup>
        )}
        <TextField
          fullWidth
          multiline
          minRows={2}
          maxRows={6}
          label="Notes"
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          sx={{ mt: 2 }}
        />
        {error && (
          <Alert severity="error" sx={{ mt: 1 }}>
            {error}
          </Alert>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={ctx.dismissDisposition} disabled={submitting}>
          Skip
        </Button>
        <Button
          variant="contained"
          onClick={onSubmit}
          disabled={loading || submitting || !selected}
        >
          {submitting ? 'Saving…' : 'Save wrap-up'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
